<script setup>
import logoMain from '@/assets/images/logo-main.svg'
import { useHead } from '@unhead/vue'
import { storeToRefs } from 'pinia'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  ref,
  watch
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useCheckoutSteps } from '@/composables/useCheckoutSteps.js'
import { TYPES as adminTool } from '@/constants/AdminTool'
import { CHECKOUT_GUEST_STEPS } from '@/constants/CheckoutConstants.js'
import { translate } from '@/plugins/i18n.js'
import { NO_INDEX_FOLLOW } from '@/services/helpers/MetaHelper.js'

const TheCheckoutDelivery = defineAsyncComponent(
  () => import('@/components/checkout/organisms/TheCheckoutDelivery.vue')
)
const TheCheckoutPayment = defineAsyncComponent(
  () => import('@/components/checkout/organisms/TheCheckoutPayment.vue')
)
const TheCheckoutReview = defineAsyncComponent(
  () => import('@/components/checkout/organisms/TheCheckoutReview.vue')
)
const TheCheckoutSummary = defineAsyncComponent(
  () => import('@/components/checkout/organisms/TheCheckoutSummary.vue')
)
const TheCheckoutStepper = defineAsyncComponent(
  () => import('@/components/checkout/molecules/TheCheckoutStepper.vue')
)
const TheOpcErrorMessage = defineAsyncComponent(
  () => import('@/components/opc/atoms/TheOpcErrorMessage.vue')
)
const TheCheckoutAddressAdd = defineAsyncComponent(
  () => import('@/components/checkout/organisms/TheCheckoutAddressAdd.vue')
)
const TheCheckoutPaymentAdd = defineAsyncComponent(
  () => import('@/components/checkout/organisms/TheCheckoutPaymentAdd.vue')
)

import { useCartStore } from '@/stores/cart.js'
import { useCustomerStore } from '@/stores/customer'
import { useGuestCheckoutStore } from '@/stores/guestCheckout'
import { useNewCheckoutStore } from '@/stores/newCheckout.ts'
import { useDisplay } from 'vuetify'

const MultiTools = defineAsyncComponent(
  () => import('@/components/basic/molecules/MultiTools.vue')
)
const Tool = defineAsyncComponent(
  () => import('@/components/basic/atoms/Tool.vue')
)

const loading = ref(true)
const route = useRoute()

const { mdAndUp } = useDisplay()

const logoSrc = logoMain

const {
  addressType,
  getDefaultStep,
  selectedDeliveryOptionsMap,
  deliveryOptions,
  hasPaymentMethodSelected,
  hasAddressesSelected
} = useCheckoutSteps()

// Store refs
const customerStore = useCustomerStore()
const checkoutStore = useNewCheckoutStore()
const guestStore = useGuestCheckoutStore()
const cartStore = useCartStore()

// Use computed to safely access checkoutError to avoid storeToRefs issues with null values
const checkoutError = computed(() => checkoutStore.checkoutError)

const { customerIsAdmin, profile, profileV2 } = storeToRefs(customerStore)
const { instance } = storeToRefs(cartStore)

const router = useRouter()

const stepComponents = {
  [CHECKOUT_GUEST_STEPS.ADDRESS_ACTION]: TheCheckoutAddressAdd,
  [CHECKOUT_GUEST_STEPS.PAYMENT]: TheCheckoutPayment,
  [CHECKOUT_GUEST_STEPS.PAYMENT_ACTION]: TheCheckoutPaymentAdd,
  [CHECKOUT_GUEST_STEPS.DELIVERY]: TheCheckoutDelivery,
  [CHECKOUT_GUEST_STEPS.REVIEW]: TheCheckoutReview
}
const GUEST_REVIEW_STEP = 'checkout_guest_review'

const currentStep = computed(() => {
  return route.name
})

const currentComponent = computed(() => {
  return stepComponents[currentStep.value] || TheCheckoutAddressAdd
})

useHead({
  title: computed(() => translate('checkout:page_title')),
  meta: [{ name: 'robots', content: NO_INDEX_FOLLOW }]
})

const setDefaults = () => {
  // For guest checkout, initialize useSameBillingAddress to true if not set
  if (guestStore.useSameBillingAddress === null) {
    guestStore.useSameBillingAddress = true
  }
}

onMounted(async () => {
  await cartStore.fetchDeliveryOptions()

  setDefaults()

  await nextTick()

  if (route.query.error) {
    checkoutStore.checkoutError = route.query.error
  }

  const defaultStep = getDefaultStep()
  if (defaultStep !== currentStep.value) {
    const params = {}
    if (defaultStep === CHECKOUT_GUEST_STEPS.ADDRESS_ACTION) {
      // Only set type param if addressType is not null, otherwise default to 'shipping'
      const typeValue = addressType.value
      if (typeValue === 'shipping' || typeValue === 'billing') {
        params.type = typeValue
      } else {
        params.type = 'shipping'
      }
    }
    if (
      defaultStep === CHECKOUT_GUEST_STEPS.ADDRESS_ACTION ||
      defaultStep === CHECKOUT_GUEST_STEPS.PAYMENT_ACTION
    ) {
      params.action = 'add'
    }
    router.push({
      name: defaultStep,
      params
    })
  }

  loading.value = false
})

watch(
  () => route.name,
  (name) => {
    if (name === 'checkout_guest_default') {
      const defaultStep = getDefaultStep()
      if (defaultStep !== name) {
        const params = {}
        if (defaultStep === CHECKOUT_GUEST_STEPS.ADDRESS_ACTION) {
          // Only set type param if addressType is not null, otherwise default to 'shipping'
          const typeValue = addressType.value
          if (typeValue === 'shipping' || typeValue === 'billing') {
            params.type = typeValue
          } else {
            params.type = 'shipping'
          }
        }
        if (
          defaultStep === CHECKOUT_GUEST_STEPS.ADDRESS_ACTION ||
          defaultStep === CHECKOUT_GUEST_STEPS.PAYMENT_ACTION
        ) {
          params.action = 'add'
        }
        router.push({
          name: defaultStep,
          params
        })
      }
    }
  }
)
watch(
  () => route.query.error,
  (error) => {
    if (error && !checkoutError.value) {
      checkoutStore.checkoutError = error
    }
  },
  { immediate: true }
)

const elbContext = computed(() => {
  if (!cartStore) return ''

  const parts = []

  if (instance?.value?.grandTotal) {
    parts.push(`grand_total:${instance?.value?.grandTotal}`)
  }
  if (instance?.value?.vendors?.length) {
    parts.push(`vendor_count:${instance?.value?.vendors?.length}`)
  }
  if (instance?.value?.shippingTotal) {
    parts.push(`shipping:${instance?.value?.shippingTotal}`)
  }

  return parts.join(';')
})
</script>

<template>
  <div
    data-elb="checkout"
    data-elbcontext="shopping:checkout"
    :data-elb-checkout="elbContext"
    id="checkout-wrapper"
  >
    <VContainer v-if="!loading" class="bg-white px-0 px-sm-3" fluid>
      <VRow class="justify-center">
        <VCol cols="12" md="8" class="pa-0">
          <header
            id="header-wrapper"
            class="d-flex flex-column w-100 flex-md-row bg-white align-center justify-center py-3"
          >
            <a
              href="/checkout/cart"
              @click.prevent="router.push({ name: 'cart' })"
              class="d-inline-block"
            >
              <VImg
                itemscope
                itemtype="https://schema.org/Brand"
                :src="logoSrc"
                class="header-logo"
                alt="Vicampo"
              />
            </a>
            <div class="w-md-75 w-100 px-md-8 px-0 mt-3">
              <TheCheckoutStepper :current-step="currentStep" />
            </div>
          </header>
        </VCol>
      </VRow>
    </VContainer>
    <TheOpcErrorMessage class="mb-3" />
    <VContainer v-if="loading || currentStep === 'checkout_guest_default'">
      <VRow>
        <VCol cols="12" class="text-center justify-center">
          <h2 class="my-5 px-3 font-weight-bold font-size-xxl">
            {{ translate('checkout.loading') }}
          </h2>
          <VBtn color="white" icon rounded="50%" width="56" height="56" loading>
            <VIcon>custom:reset</VIcon>
          </VBtn>
        </VCol>
      </VRow>
    </VContainer>
    <VContainer v-else class="px-0">
      <VRow v-if="!mdAndUp" no-gutters>
        <VCol cols="12">
          <TheCheckoutSummary
            :isReviewStep="currentStep === GUEST_REVIEW_STEP"
          />
        </VCol>
      </VRow>
      <VRow class="pt-3">
        <template v-if="mdAndUp">
          <VCol :cols="currentStep === GUEST_REVIEW_STEP ? 6 : 7">
            <component
              :is="currentComponent"
              :type="route.params.type"
              :action="route.params.action"
              @change="setDefaults"
            />
          </VCol>
          <VCol
            :cols="currentStep === GUEST_REVIEW_STEP ? 6 : 5"
            class="pl-3 mt-md-16"
          >
            <TheCheckoutSummary
              :isReviewStep="currentStep === GUEST_REVIEW_STEP"
            />
          </VCol>
        </template>
        <template v-else>
          <VCol cols="12">
            <component
              :is="currentComponent"
              :type="route.params.type"
              @change="setDefaults"
            />
          </VCol>
        </template>
      </VRow>
    </VContainer>
    <MultiTools id="fixed-tools" v-if="customerIsAdmin">
      <Tool :type="adminTool.TRANSLATE_MODE" />
      <Tool :type="adminTool.HIGHLIGHT_CMS_ITEMS" />
      <Tool :type="adminTool.TOGGLE_DATALAYER_LOGGING" />
      <Tool :type="adminTool.TOGGLE_ELBWALKER_LOGGING" />
    </MultiTools>
  </div>
</template>

<style scoped>
.header-logo {
  width: 150px;
  height: auto;
  max-width: 100%;
}

.display-md-and-up .header-logo {
  width: 200px;
}
</style>
